/**
 * Credit Notes List Page
 * HTMX-driven filtering & pagination.
 * Retains SearchPicker for customer autocomplete and bulk PDF selection.
 */

import { SearchPicker } from '../components/SearchPicker.js';
import {
  renderCustomerItem,
  isCustomerDisabled,
  PICKER_MODE_FILTER,
} from '../utils/ui/customer-picker.js';
import {
  parseMoney,
  formatMoney,
  roundCents,
  sanitizeMoneyField as sanitizeMoney,
} from '../utils/forms/money.js';

let customerPicker = null;

/**
 * Initialize the credit notes list page
 */
export function initCreditNotesList(options = {}) {
  const { hasActiveFilters = false, urls = {} } = options;

  initFilterPanelUI(hasActiveFilters);
  initCustomerPicker(urls);
  initDatepickerBridge();
  initBulkSelection();
  bindCreditNotesActionsOnce();
}

/**
 * Filter panel toggle / reset (HTMX handles requests)
 */
function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = document.getElementById('filterForm');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  // Per-node idempotency flag as a plain JS property (not dataset) so
  // it does NOT serialize into HTMX history snapshots; this guarantees
  // that when hx-boost restores a cached DOM, the newly-inserted
  // button nodes get a fresh listener instead of silently inheriting
  // the "already bound" flag from the snapshot.
  if (toggleBtn && panel && !toggleBtn._cnFilterBound) {
    toggleBtn._cnFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._cnResetBound) {
    resetBtn._cnResetBound = true;
    resetBtn.addEventListener('click', () => {
      form.querySelectorAll('select.form-select').forEach((sel) => {
        sel.value = '';
        const wrapper = sel.closest('.custom-select-wrapper');
        const textEl = wrapper?.querySelector('.custom-select-text');
        if (textEl && sel.options[0]) textEl.textContent = sel.options[0].text;
      });

      form.querySelectorAll('input[type="text"]').forEach((inp) => {
        inp.value = '';
      });

      form.querySelectorAll('input[type="hidden"]').forEach((inp) => {
        inp.value = '';
      });

      if (customerPicker) customerPicker.clear();

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

/**
 * Initialize customer search picker
 */
function initCustomerPicker(urls) {
  const searchInput = document.getElementById('customer-search');
  if (!searchInput) return;

  customerPicker = new SearchPicker({
    inputId: 'customer-search',
    hiddenInputId: 'customer-filter',
    dropdownId: 'customer-search-results',
    endpoint: (urls.customerSearch || '/office/api/customers/search/') + '?include=all',
    maxResults: 20,
    minChars: 0,
    renderItem: (item) => renderCustomerItem(item, { mode: PICKER_MODE_FILTER }),
    isItemDisabled: (item) => isCustomerDisabled(item, PICKER_MODE_FILTER),
    onSelect: () => {
      const form = document.getElementById('filterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    onClear: () => {
      const form = document.getElementById('filterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
  });
}

/**
 * Bridge Flowbite datepicker changeDate → native change for HTMX
 */
function initDatepickerBridge() {
  document.querySelectorAll('[datepicker]').forEach((el) => {
    if (el._cnDpBound) return;
    el._cnDpBound = true;
    el.addEventListener('changeDate', () => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

// renderCustomerItem moved to ``frontend/src/utils/ui/customer-picker.js``.

let bulkInitialized = false;

/**
 * Bulk checkbox selection for PDF download.
 * Uses document-level event delegation so it survives HTMX swaps.
 * Safe to call multiple times — listeners added only once, state always refreshed.
 */
export function initBulkSelection() {
  const btn = document.getElementById('bulk-pdf-btn');
  const countSpan = document.getElementById('bulk-pdf-count');
  const form = document.getElementById('bulk-pdf-form');

  if (!btn || !form) return;

  function updateState() {
    const all = document.querySelectorAll('.cn-row-checkbox');
    const checked = document.querySelectorAll('.cn-row-checkbox:checked');
    const count = checked.length;

    btn.disabled = count === 0;
    btn.classList.toggle('opacity-50', count === 0);
    btn.classList.toggle('cursor-not-allowed', count === 0);
    btn.classList.toggle('cursor-pointer', count > 0);
    if (countSpan) countSpan.textContent = count > 0 ? ` (${count})` : '';

    const selectAll = document.getElementById('cn-select-all');
    if (selectAll) {
      selectAll.checked = all.length > 0 && count === all.length;
      selectAll.indeterminate = count > 0 && count < all.length;
    }
  }

  if (!bulkInitialized) {
    document.addEventListener('change', (e) => {
      if (e.target.matches('#cn-select-all')) {
        const isChecked = e.target.checked;
        document.querySelectorAll('.cn-row-checkbox').forEach((cb) => { cb.checked = isChecked; });
        updateState();
      } else if (e.target.matches('.cn-row-checkbox')) {
        updateState();
      }
    });

    btn.addEventListener('click', () => {
      const checked = document.querySelectorAll('.cn-row-checkbox:checked');
      if (!checked.length) return;

      form.querySelectorAll('input[name="credit_note_ids"]').forEach((el) => el.remove());
      checked.forEach((cb) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'credit_note_ids';
        input.value = cb.value;
        form.appendChild(input);
      });
      form.submit();
    });

    bulkInitialized = true;
  }

  updateState();
}

// Money parse/format/sanitize live in utils/forms/money.js (shared
// with job-form, invoice-detail and the global data-money-input
// sanitizer in app.js). Display uses the locale comma; the server
// widget (DecimalTextInput) normalises back to a dot on POST.

// Display the effective rate without spurious decimals: 21 → "21",
// 17.35 → "17,35".
function formatRate(ratio) {
  const pct = ratio * 100;
  const rounded = Math.round(pct * 100) / 100;
  return String(rounded).replace('.', ',');
}

/**
 * Credit-note create modal — bidirectional HT ↔ TTC, same UX as the
 * job billing card: the operator types EITHER the net amount OR the
 * VAT-inclusive total and the other recomputes at the invoice's
 * effective VAT rate (proportional, so mixed-rate invoices are
 * handled correctly). The VAT amount is never typed: the server
 * derives it in CreditNoteForm.clean(); here it only feeds the
 * « of which VAT … » hint. The TTC input has no name attribute and
 * is never posted.
 */
export function initCreditNoteCreate(options = {}) {
  const { invoiceSubtotal = 0, invoiceVat = 0, maxNetAmount = 0 } = options;

  const reasonSelect = document.getElementById('id_reason');
  const amountInput = document.getElementById('id_amount');
  const ttcInput = document.getElementById('id_cn_total_ttc');
  const vatHint = document.getElementById('cn-vat-hint');
  if (!reasonSelect || !amountInput || !ttcInput) return;

  const vatRate = invoiceSubtotal > 0 ? invoiceVat / invoiceSubtotal : 0;
  const maxTtc = maxNetAmount * (1 + vatRate);

  // Hidden mirror so the cancellation case still posts ``amount``
  // while the visible (frozen) input is disabled.
  const hiddenAmount = document.createElement('input');
  hiddenAmount.type = 'hidden';
  hiddenAmount.name = 'amount';
  hiddenAmount.disabled = true;
  amountInput.parentNode.appendChild(hiddenAmount);

  function updateVatHint(vatAmount) {
    if (!vatHint) return;
    if (vatAmount == null) {
      vatHint.hidden = true;
      return;
    }
    const tpl = vatHint.dataset.hintTemplate || 'of which VAT ({rate} %): {amount} €';
    vatHint.textContent = (vatHint.dataset.prefix || '')
      + tpl.replace('{rate}', formatRate(vatRate))
           .replace('{amount}', formatMoney(vatAmount));
    vatHint.hidden = false;
  }

  // TTC is built as net + roundCents(VAT) — NOT net × (1 + rate) — so
  // the displayed total always equals what clean() will store
  // (e.g. 100,50 × 21 %: VAT 21,11 → TTC 121,61, not 121,60).

  // Forward: HT typed → clamp to max available, derive TTC + hint.
  function syncFromHt() {
    let net = parseMoney(amountInput.value);
    if (Number.isNaN(net)) {
      ttcInput.value = '';
      updateVatHint(null);
      return;
    }
    if (maxNetAmount > 0 && net > maxNetAmount) {
      net = maxNetAmount;
      amountInput.value = formatMoney(net);
    }
    const vat = roundCents(net * vatRate);
    ttcInput.value = formatMoney(net + vat);
    updateVatHint(vat);
  }

  // Reverse: TTC typed → back-calculate HT. Writing
  // ``amountInput.value`` programmatically does not fire ``input``,
  // so the two directions never loop into each other.
  function syncFromTtc() {
    let total = parseMoney(ttcInput.value);
    if (Number.isNaN(total)) {
      amountInput.value = '';
      updateVatHint(null);
      return;
    }
    if (maxTtc > 0 && total > maxTtc) {
      total = maxTtc;
      ttcInput.value = formatMoney(total);
    }
    const net = roundCents(total / (1 + vatRate));
    amountInput.value = formatMoney(net);
    updateVatHint(roundCents(net * vatRate));
  }

  function setFrozen(el, frozen) {
    el.disabled = frozen;
    el.style.backgroundColor = frozen ? '#f3f4f6' : '';
    el.style.color = frozen ? '#6b7280' : '';
  }

  function updateFields() {
    if (reasonSelect.value === 'cancellation') {
      // Full cancellation: amounts come verbatim from the invoice —
      // exact even on mixed-rate invoices where a single effective
      // rate could not reproduce the invoice's VAT total. The server
      // applies the same override in CreditNoteForm.clean().
      amountInput.value = formatMoney(invoiceSubtotal);
      ttcInput.value = formatMoney(invoiceSubtotal + invoiceVat);
      setFrozen(amountInput, true);
      setFrozen(ttcInput, true);
      hiddenAmount.value = formatMoney(invoiceSubtotal);
      hiddenAmount.disabled = false;
      updateVatHint(invoiceVat);
    } else {
      setFrozen(amountInput, false);
      setFrozen(ttcInput, false);
      hiddenAmount.disabled = true;
      syncFromHt();
    }
  }

  reasonSelect.addEventListener('change', updateFields);
  amountInput.addEventListener('input', () => {
    sanitizeMoney(amountInput);
    syncFromHt();
  });
  ttcInput.addEventListener('input', () => {
    sanitizeMoney(ttcInput);
    syncFromTtc();
  });
  updateFields();
}

// Module-level flag so the inner-swap hook is bound once per SPA
// lifetime and does not stack on every hx-boost re-init.
let afterSettleBound = false;

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', (e) => {
    const target = e.detail && e.detail.target;
    const id = target && target.id;
    if (id !== 'list-results' && id !== 'main-content') return;
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
    initBulkSelection();
  });
}

/**
 * Credit Notes table sort: rebuilds the query string and reloads
 * the page. Intentionally does NOT use hx-boost — the credit-notes
 * list uses HTMX only for the filter form, whereas sorting is wired
 * via full page nav so the URL is the single source of truth.
 *
 * Wired declaratively via ``data-credit-notes-action="sort"`` +
 * ``data-sort-key`` on the table header anchors (Phase 21.A,
 * replacing the legacy ``onclick="sortCnTable('…')"``). Still
 * exported for tests and for the (now historical) ``window.*``
 * API consumers — see ``index.js``.
 */
export function sortCnTable(field) {
  const params = new URLSearchParams(window.location.search);
  const curSort = params.get('sort') || 'issue_date';
  const curOrder = params.get('order') || 'desc';
  if (curSort === field) {
    params.set('order', curOrder === 'asc' ? 'desc' : 'asc');
  } else {
    params.set('sort', field);
    params.set('order', 'asc');
  }
  params.delete('page');
  window.location.search = params.toString();
}

/**
 * Auto-init entry point. Invoked by _runAutoInit (app.js) on every
 * htmx:afterSwap of #main-content and on htmx:historyRestore, wired
 * through ``data-auto-init="creditNotesList"`` in the template.
 */
export function autoInitCreditNotesList() {
  const root = document.querySelector('[data-auto-init="creditNotesList"]');
  if (!root) return;
  initCreditNotesList({
    hasActiveFilters: root.dataset.hasActiveFilters === '1',
    urls: {
      customerSearch: root.dataset.customerSearchUrl || '/office/api/customers/search/',
    },
  });
  bindAfterSettleOnce();
}

// Delegate at document level for the sort header anchors. Phase 21.A
// migration of the two inline ``onclick="sortCnTable('…')"`` triggers.
// Idempotent — bound once per SPA lifetime via ``_cnActionsBound``.
//
// Bound in the CAPTURE phase to match the rest of the action
// delegates in the codebase (``confirm-action.js``,
// ``cleaners.js``, …). Sort headers don't currently sit under a
// ``data-stop-propagation`` ancestor, but capture-phase keeps the
// contract consistent so future row-level stoppers can't silently
// break sorting.
let _cnActionsBound = false;

function bindCreditNotesActionsOnce() {
  if (_cnActionsBound) return;
  _cnActionsBound = true;

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-credit-notes-action]');
    if (!trigger) return;
    const action = trigger.dataset.creditNotesAction;
    if (action !== 'sort') return;
    const key = trigger.dataset.sortKey;
    if (!key) return;
    event.preventDefault();
    sortCnTable(key);
  }, true);
}

window.initCreditNotesList = initCreditNotesList;
window.initCreditNoteCreate = initCreditNoteCreate;
